import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["New", "Reviewing", "DOJ Stay Requested", "POC Filed", "POI In Execution", "Approved & Compliant"];

async function authedFetch(url, options = {}) {
  let res = await fetch(url, { credentials: "include", ...options });
  if (res.status === 401) {
    const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
    if (r.ok) res = await fetch(url, { credentials: "include", ...options });
  }
  return res;
}

export default function AdminDashboard() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("All");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const res = await authedFetch(`${API}/intake`);
    if (res.status === 401) {
      navigate("/admin/login");
      return;
    }
    setItems(await res.json());
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    const res = await authedFetch(`${API}/intake/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Status updated to "${status}"`);
    } else {
      toast.error("Could not update status");
    }
  };

  const deleteItem = async (id) => {
    const res = await authedFetch(`${API}/intake/${id}`, { method: "DELETE" });
    if (res.ok) {
      setItems((list) => list.filter((it) => it.id !== id));
      toast.success("Submission deleted");
    } else {
      toast.error("Could not delete submission");
    }
    setConfirmDeleteId(null);
  };

  const filtered = (items || []).filter((it) => filter === "All" || it.facility_type.startsWith(filter));

  const exportCsv = () => {
    const rows = [
      ["Facility", "Type", "Size", "Contact", "Email", "Phone", "Service", "Citation Status", "Deadline", "Status", "Submitted"],
      ...filtered.map((it) => [
        it.facility_name, it.facility_type, it.facility_size, it.contact_name, it.email, it.phone,
        it.service_needed, it.citation_status, it.target_deadline, it.status, it.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "hale-pono-intake.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = [
    { label: "Total Cases", value: (items || []).length },
    { label: "New", value: (items || []).filter((i) => i.status === "New").length },
    { label: "ICF", value: (items || []).filter((i) => i.facility_type.includes("ICF")).length },
    { label: "IDD", value: (items || []).filter((i) => i.facility_type.includes("IDD")).length },
  ];

  return (
    <div className="min-h-screen bg-basalt" data-testid="admin-dashboard">
      <header className="border-b border-sand/10 bg-abyss">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="flex items-center gap-2 rounded-full border border-sand/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mist transition-colors duration-300 hover:text-sand"
              data-testid="dashboard-refresh-button"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-ochre/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ochre transition-[background-color,color] duration-300 hover:bg-ochre hover:text-basalt"
              data-testid="dashboard-logout-button"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <h1 className="font-display text-2xl font-bold text-sand sm:text-3xl" data-testid="dashboard-heading">
          Compliance Intake Dashboard
        </h1>
        <p className="mt-2 text-sm text-mist">Facility cases submitted through the emergency intake portal.</p>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="dashboard-stats">
          {stats.map((s) => (
            <div key={s.label} className="glass-card rounded-2xl p-6">
              <span className="font-display text-3xl font-extrabold text-ochre">{s.value}</span>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2" data-testid="dashboard-filters">
            {["All", "ICF", "IDD"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-full px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-[background-color,color,border-color] duration-300 ${
                  filter === f ? "bg-ochre text-basalt" : "border border-sand/20 text-mist hover:text-sand"
                }`}
                data-testid={`filter-${f.toLowerCase()}-button`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            onClick={exportCsv}
            className="flex items-center gap-2 rounded-full border border-sand/20 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mist transition-colors duration-300 hover:border-ochre/50 hover:text-ochre"
            data-testid="export-csv-button"
          >
            <Download size={13} /> Export CSV
          </button>
        </div>

        <div className="glass-card mt-6 overflow-x-auto rounded-2xl" data-testid="dashboard-table-wrapper">
          {items === null ? (
            <p className="p-10 text-sm text-mist" data-testid="dashboard-loading">Loading submissions...</p>
          ) : filtered.length === 0 ? (
            <p className="p-10 text-sm text-mist" data-testid="dashboard-empty">No intake submissions yet.</p>
          ) : (
            <table className="w-full min-w-[900px] text-left text-sm" data-testid="dashboard-table">
              <thead>
                <tr className="border-b border-sand/10 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                  <th className="px-5 py-4">Facility</th>
                  <th className="px-5 py-4">Type / Size</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Deadline</th>
                  <th className="px-5 py-4">Submitted</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-sand/5 align-top transition-colors duration-300 hover:bg-sand/5" data-testid={`intake-row-${it.id}`}>
                    <td className="px-5 py-4 font-semibold text-sand">{it.facility_name}</td>
                    <td className="px-5 py-4 text-mist">
                      {it.facility_type}
                      <span className="block text-xs text-smoke">{it.facility_size}</span>
                    </td>
                    <td className="px-5 py-4 text-mist">
                      {it.contact_name}
                      <span className="block text-xs text-smoke">{it.email}</span>
                      <span className="block text-xs text-smoke">{it.phone}</span>
                    </td>
                    <td className="px-5 py-4 text-mist">
                      {it.service_needed}
                      {it.citation_status && <span className="block max-w-[220px] truncate text-xs text-smoke">{it.citation_status}</span>}
                    </td>
                    <td className="px-5 py-4 text-mist">{it.target_deadline || "—"}</td>
                    <td className="px-5 py-4 text-mist">{new Date(it.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4">
                      <select
                        value={it.status}
                        onChange={(e) => updateStatus(it.id, e.target.value)}
                        className="rounded-lg border border-sand/15 bg-basalt px-3 py-2 text-xs text-sand outline-none transition-colors duration-300 focus:border-ochre/60"
                        data-testid={`status-select-${it.id}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      {confirmDeleteId === it.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => deleteItem(it.id)}
                            className="rounded-full bg-red-500 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white transition-colors duration-300 hover:bg-red-400"
                            data-testid={`confirm-delete-${it.id}`}
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="rounded-full border border-sand/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-mist transition-colors duration-300 hover:text-sand"
                            data-testid={`cancel-delete-${it.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(it.id)}
                          className="flex items-center gap-2 rounded-full border border-sand/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-mist transition-[color,border-color] duration-300 hover:border-red-500/50 hover:text-red-400"
                          data-testid={`delete-button-${it.id}`}
                          aria-label={`Delete ${it.facility_name}`}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
